import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getCoachAthletesWithMetrics,
  getCoachMetricsReminder,
  dismissMetricsReminder,
  addAthleteMetrics,
  getAthleteMetricsChartData,
  type CoachAthletesWithMetrics,
  type MetricsReminder,
  type MetricsChartData,
} from "../../../services/api";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameButton, GameCard, GameIcon } from "../../../components/game";

export default function AppCoachMetrics() {
  const { t } = useTranslation();

  const [data, setData] = useState<CoachAthletesWithMetrics | null>(null);
  const [reminder, setReminder] = useState<MetricsReminder | null>(null);
  const [loading, setLoading] = useState(true);
  const [teamFilter, setTeamFilter] = useState("");
  const [teamMenuOpen, setTeamMenuOpen] = useState(false);
  const [showOutdatedOnly, setShowOutdatedOnly] = useState(false);

  // Add metrics modal
  const [metricsModal, setMetricsModal] = useState<{
    open: boolean;
    athleteId: string;
    athleteName: string;
  } | null>(null);
  const [metricsForm, setMetricsForm] = useState({
    height_cm: "",
    weight_kg: "",
    wingspan_cm: "",
    reach_cm: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  // Chart modal
  const [chartModal, setChartModal] = useState<{
    open: boolean;
    athleteId: string;
    athleteName: string;
    data: MetricsChartData | null;
  } | null>(null);
  const [loadingChart, setLoadingChart] = useState(false);

  useEffect(() => {
    loadData();
  }, [teamFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [athletesData, reminderData] = await Promise.all([
        getCoachAthletesWithMetrics(teamFilter || undefined),
        getCoachMetricsReminder(),
      ]);
      setData(athletesData);
      setReminder(reminderData);
    } catch (err) {
      console.error("Failed to load data:", err);
    }
    setLoading(false);
  };

  const handleDismissReminder = async () => {
    try {
      await dismissMetricsReminder();
      const updated = await getCoachMetricsReminder();
      setReminder(updated);
    } catch (err) {
      console.error("Failed to dismiss reminder:", err);
    }
  };

  const filteredAthletes = data?.athletes.filter(a =>
    !showOutdatedOnly || a.metrics_outdated
  ) || [];

  const handleOpenMetricsModal = (athleteId: string, athleteName: string) => {
    setMetricsModal({ open: true, athleteId, athleteName });
    setMetricsForm({ height_cm: "", weight_kg: "", wingspan_cm: "", reach_cm: "", notes: "" });
  };

  const handleSaveMetrics = async () => {
    if (!metricsModal) return;
    setSaving(true);
    try {
      await addAthleteMetrics(metricsModal.athleteId, {
        height_cm: metricsForm.height_cm ? Number(metricsForm.height_cm) : undefined,
        weight_kg: metricsForm.weight_kg ? Number(metricsForm.weight_kg) : undefined,
        wingspan_cm: metricsForm.wingspan_cm ? Number(metricsForm.wingspan_cm) : undefined,
        reach_cm: metricsForm.reach_cm ? Number(metricsForm.reach_cm) : undefined,
        notes: metricsForm.notes || undefined,
      });
      setMetricsModal(null);
      await loadData();
    } catch (err) {
      console.error("Failed to save metrics:", err);
    }
    setSaving(false);
  };

  const handleOpenChart = async (athleteId: string, athleteName: string) => {
    setChartModal({ open: true, athleteId, athleteName, data: null });
    setLoadingChart(true);
    try {
      const chartData = await getAthleteMetricsChartData(athleteId);
      setChartModal(prev => prev ? { ...prev, data: chartData } : null);
    } catch (err) {
      console.error("Failed to load chart data:", err);
    }
    setLoadingChart(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-800">{t("coach.metrics", "Metrici fizice")}</h1>
        </div>

        {/* Reminder Banner */}
        {reminder?.reminder_due && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 shadow-[0_3px_0_#fde68a]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <GameIcon name="clock" className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="font-extrabold text-amber-800">
                  {t("coach.metricsReminderTitle", "Este timpul pentru măsurători!")}
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  {reminder.athletes_needing_update} {t("coach.athletesNeedUpdate", "sportivi necesita actualizare")}
                </p>
                <button
                  onClick={handleDismissReminder}
                  className="text-sm text-amber-600 font-bold mt-2 hover:text-amber-700"
                >
                  {t("coach.dismissReminder", "Am inteles")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="space-y-3">
          {data && data.teams.length > 1 && (
            /* Custom dropdown — a native <select>'s option popup renders
               mis-placed and tiny in the iOS WebView, so we roll our own
               menu that sits directly under the trigger. */
            <div className="relative">
              <button
                type="button"
                onClick={() => setTeamMenuOpen((o) => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <span className="truncate">
                  {teamFilter ? (data.teams.find((tm) => tm.id === teamFilter)?.name || t("coach.allTeams", "Toate echipele")) : t("coach.allTeams", "Toate echipele")}
                </span>
                <svg className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${teamMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {teamMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setTeamMenuOpen(false)} />
                  <div className="absolute left-0 right-0 mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-64 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => { setTeamFilter(""); setTeamMenuOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 ${!teamFilter ? "font-bold text-indigo-600 bg-indigo-50" : "text-slate-700"}`}
                    >
                      {t("coach.allTeams", "Toate echipele")}
                    </button>
                    {data.teams.map((team) => (
                      <button
                        key={team.id}
                        type="button"
                        onClick={() => { setTeamFilter(team.id); setTeamMenuOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 ${teamFilter === team.id ? "font-bold text-indigo-600 bg-indigo-50" : "text-slate-700"}`}
                      >
                        {team.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showOutdatedOnly}
                onChange={e => setShowOutdatedOnly(e.target.checked)}
                className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
              />
              <span className="text-sm text-slate-700">
                {t("coach.showOutdatedOnly", "Doar sportivi care necesita actualizare")}
              </span>
            </label>
          </div>
        </div>

        {/* Athletes List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredAthletes.length === 0 ? (
          <GameCard className="p-8 text-center">
            <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-slate-500">{t("coach.noAthletes", "Niciun sportiv găsit")}</p>
          </GameCard>
        ) : (
          <div className="space-y-3">
            {filteredAthletes.map(athlete => (
              <GameCard key={athlete.id} className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-extrabold ${
                    athlete.metrics_outdated
                      ? "bg-gradient-to-br from-amber-500 to-orange-600"
                      : "bg-gradient-to-br from-indigo-500 to-blue-600"
                  }`}>
                    {athlete.full_name?.charAt(0) || "?"}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-extrabold text-slate-800">{athlete.full_name}</h3>
                    <p className="text-xs text-slate-500">
                      {athlete.athlete_profiles?.team?.name || t("coach.noTeam", "Fără echipa")}
                    </p>
                  </div>
                  {athlete.metrics_outdated && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                      {t("coach.outdated", "Necesita actualizare")}
                    </span>
                  )}
                </div>

                {/* Latest Metrics */}
                {athlete.latest_metrics ? (
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-xs text-slate-500">{t("coach.height", "Înălțime")}</p>
                      <p className="font-semibold text-slate-800">
                        {athlete.latest_metrics.height_cm ? `${athlete.latest_metrics.height_cm} cm` : "-"}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-xs text-slate-500">{t("coach.weight", "Greutate")}</p>
                      <p className="font-semibold text-slate-800">
                        {athlete.latest_metrics.weight_kg ? `${athlete.latest_metrics.weight_kg} kg` : "-"}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-xs text-slate-500">{t("coach.wingspan", "Anvergură")}</p>
                      <p className="font-semibold text-slate-800">
                        {athlete.latest_metrics.wingspan_cm ? `${athlete.latest_metrics.wingspan_cm} cm` : "-"}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-xs text-slate-500">{t("coach.reach", "Alonjă")}</p>
                      <p className="font-semibold text-slate-800">
                        {athlete.latest_metrics.reach_cm ? `${athlete.latest_metrics.reach_cm} cm` : "-"}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-lg p-3 mb-3 text-center">
                    <p className="text-sm text-slate-500">{t("coach.noMetricsYet", "Nicio măsurare inregistrata")}</p>
                  </div>
                )}

                {/* Last measured date */}
                {athlete.latest_metrics && (
                  <p className="text-xs text-slate-500 mb-3">
                    {t("coach.lastMeasured", "Ultima măsurare")}: {formatDate(athlete.latest_metrics.measured_at)}
                  </p>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <GameButton
                    variant="primary"
                    onClick={() => handleOpenMetricsModal(athlete.id, athlete.full_name)}
                    className="flex-1 px-4 py-2.5 text-sm"
                  >
                    {t("coach.addMeasurement", "Adauga măsurare")}
                  </GameButton>
                  {athlete.latest_metrics && (
                    <GameButton
                      variant="ghost"
                      onClick={() => handleOpenChart(athlete.id, athlete.full_name)}
                      className="w-auto px-4 py-2.5"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                      </svg>
                    </GameButton>
                  )}
                </div>
              </GameCard>
            ))}
          </div>
        )}

        {/* Add Metrics Modal */}
        {metricsModal && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)] sm:pt-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:pb-4 !mt-0">
            {/* Same three-part shell as the payment modal: fixed header, fixed
                buttons, scrolling middle. This form is the tallest one in the
                coach app and its Save button was below the fold. */}
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl flex flex-col max-h-full">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">{t("coach.addMeasurement", "Adauga măsurare")}</h2>
                  <p className="text-sm text-slate-500">{metricsModal.athleteName}</p>
                </div>
                <button
                  onClick={() => setMetricsModal(null)}
                  className="p-2 hover:bg-slate-100 rounded-full"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 min-h-0 p-5 space-y-4 overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.height", "Înălțime")} (cm)
                    </label>
                    <input
                      type="number"
                      value={metricsForm.height_cm}
                      onChange={e => setMetricsForm({ ...metricsForm, height_cm: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="ex: 165"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.weight", "Greutate")} (kg)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={metricsForm.weight_kg}
                      onChange={e => setMetricsForm({ ...metricsForm, weight_kg: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="ex: 55.5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.wingspan", "Anvergură")} (cm)
                    </label>
                    <input
                      type="number"
                      value={metricsForm.wingspan_cm}
                      onChange={e => setMetricsForm({ ...metricsForm, wingspan_cm: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="ex: 168"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.reach", "Alonjă")} (cm)
                    </label>
                    <input
                      type="number"
                      value={metricsForm.reach_cm}
                      onChange={e => setMetricsForm({ ...metricsForm, reach_cm: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="ex: 210"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("coach.notes", "Note")}
                  </label>
                  <textarea
                    value={metricsForm.notes}
                    onChange={e => setMetricsForm({ ...metricsForm, notes: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    rows={2}
                    placeholder={t("coach.metricsNotesPlaceholder", "Observații suplimentare...")}
                  />
                </div>
              </div>

              <div className="px-5 py-4 border-t border-slate-100 flex gap-3 shrink-0">
                <GameButton variant="ghost" onClick={() => setMetricsModal(null)}>
                  {t("common.cancel", "Anulează")}
                </GameButton>
                <GameButton variant="primary" onClick={handleSaveMetrics} disabled={saving}>
                  {saving ? t("common.saving", "Se salvează...") : t("common.save", "Salvează")}
                </GameButton>
              </div>
            </div>
          </div>
        )}

        {/* Chart Modal */}
        {chartModal && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)] sm:pt-4 !mt-0">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl max-h-full overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">{t("coach.evolutionChart", "Evoluție")}</h2>
                  <p className="text-sm text-slate-500">{chartModal.athleteName}</p>
                </div>
                <button
                  onClick={() => setChartModal(null)}
                  className="p-2 hover:bg-slate-100 rounded-full"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-5 overflow-y-auto" style={{ maxHeight: "calc(90dvh - 80px)" }}>
                {loadingChart ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : chartModal.data && chartModal.data.raw.length > 0 ? (
                  <div className="space-y-6">
                    {/* Simple table view of metrics history */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-3 text-left font-medium text-slate-600">{t("coach.date", "Data")}</th>
                            <th className="px-4 py-3 text-center font-medium text-slate-600">{t("coach.height", "Înălțime")}</th>
                            <th className="px-4 py-3 text-center font-medium text-slate-600">{t("coach.weight", "Greutate")}</th>
                            <th className="px-4 py-3 text-center font-medium text-slate-600">{t("coach.wingspan", "Anvergură")}</th>
                            <th className="px-4 py-3 text-center font-medium text-slate-600">{t("coach.reach", "Alonjă")}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {chartModal.data.raw.slice().reverse().map((m, i) => (
                            <tr key={m.id} className={i === 0 ? "bg-emerald-50" : ""}>
                              <td className="px-4 py-3 text-slate-700">{formatDate(m.measured_at)}</td>
                              <td className="px-4 py-3 text-center">{m.height_cm ? `${m.height_cm} cm` : "-"}</td>
                              <td className="px-4 py-3 text-center">{m.weight_kg ? `${m.weight_kg} kg` : "-"}</td>
                              <td className="px-4 py-3 text-center">{m.wingspan_cm ? `${m.wingspan_cm} cm` : "-"}</td>
                              <td className="px-4 py-3 text-center">{m.reach_cm ? `${m.reach_cm} cm` : "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Progress indicators */}
                    {chartModal.data.raw.length > 1 && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {[
                          { key: "height", label: t("coach.height", "Înălțime"), unit: "cm", data: chartModal.data.datasets.height },
                          { key: "weight", label: t("coach.weight", "Greutate"), unit: "kg", data: chartModal.data.datasets.weight },
                          { key: "wingspan", label: t("coach.wingspan", "Anvergură"), unit: "cm", data: chartModal.data.datasets.wingspan },
                          { key: "reach", label: t("coach.reach", "Alonjă"), unit: "cm", data: chartModal.data.datasets.reach },
                        ].map(metric => {
                          const validData = metric.data.filter(v => v !== null) as number[];
                          if (validData.length < 2) return null;
                          const first = validData[0];
                          const last = validData[validData.length - 1];
                          const change = last - first;
                          const isPositive = change > 0;
                          return (
                            <div key={metric.key} className="bg-slate-50 rounded-xl p-4">
                              <p className="text-xs text-slate-500">{metric.label}</p>
                              <p className="text-lg font-bold text-slate-800">{last} {metric.unit}</p>
                              <div className={`flex items-center gap-1 text-sm ${isPositive ? "text-emerald-600" : "text-red-600"}`}>
                                <svg className={`w-4 h-4 ${isPositive ? "" : "rotate-180"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                </svg>
                                {Math.abs(change).toFixed(1)} {metric.unit}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-slate-500">{t("coach.noChartData", "Nu există suficiente date pentru grafic")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </MobileAppLayout>
  );
}
